
import type { FilterChangeSource } from "@/components/optimizer/query-state";
import { ANY_GAME_TIME, activeGameDays, coerceGameDays, coerceStartsWithin, gameTimeValue, localTodayYmd, type GameTime } from "@/lib/game-date";
import { MIN_PLAY_SIZE, PLAY_SIZE_MAX, WHALE_VOLUME_STEP, normalizeWhaleWatchBookKey } from "@/lib/whale-watch/whale-watch-shared";

export type SortMode = "edge" | "whale";

export interface WhaleWatchFilterState {
  searchQuery: string;
  minEdge: number;
  minWhaleVolume: number;
  minPlaySize: number;
  minOdds: number | null;
  maxOdds: number | null;
  sortMode: SortMode;
  selectedBookKeys: string[];

  sportFilter: string;
  marketTypeFilter: string;
  selectedMarketNames: string[];
  gameTime: GameTime;
}

export const FILTER_DEFAULTS: WhaleWatchFilterState = {
  searchQuery: "",
  minEdge: 0,
  minWhaleVolume: 100,
  minPlaySize: MIN_PLAY_SIZE,
  minOdds: null,
  maxOdds: null,
  sortMode: "edge",
  selectedBookKeys: [],
  sportFilter: "all",
  marketTypeFilter: "all",
  selectedMarketNames: [],
  gameTime: ANY_GAME_TIME,
};

export function coerceMinPlaySize(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return FILTER_DEFAULTS.minPlaySize;
  const stepped = Math.round(value / WHALE_VOLUME_STEP) * WHALE_VOLUME_STEP;
  return Math.max(MIN_PLAY_SIZE, Math.min(PLAY_SIZE_MAX, stepped));
}


export type WhaleWatchFiltersAction =
  | { type: "PATCH"; patch: Partial<WhaleWatchFilterState> }
  | { type: "RESET_ALL" }
  | { type: "HYDRATE"; state: WhaleWatchFilterState }
  | { type: "TOGGLE_BOOK"; bookKey: string }
  | { type: "CLEAR_BOOKS" }
  | { type: "SET_MARKET_TYPE"; value: string }
  | { type: "TOGGLE_MARKET_NAME"; marketName: string }
  | { type: "CLEAR_MARKET_NAMES" };

export function normalizeSelectedBookKeys(bookKeys: string[]): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const bookKey of bookKeys) {
    const key = normalizeWhaleWatchBookKey(bookKey);
    if (key && !seen.has(key)) {
      seen.add(key);
      normalized.push(key);
    }
  }

  return normalized;
}

export function whaleWatchFiltersReducer(
  state: WhaleWatchFilterState,
  action: WhaleWatchFiltersAction
): WhaleWatchFilterState {
  switch (action.type) {
    case "PATCH": {
      const nextState = { ...state, ...action.patch };
      if (action.patch.selectedBookKeys) {
        nextState.selectedBookKeys = normalizeSelectedBookKeys(action.patch.selectedBookKeys);
      }
      if (action.patch.minPlaySize !== undefined) {
        nextState.minPlaySize = coerceMinPlaySize(action.patch.minPlaySize);
      }
      return nextState;
    }

    case "RESET_ALL":
      return FILTER_DEFAULTS;

    case "HYDRATE":
      return {
        ...action.state,
        selectedBookKeys: normalizeSelectedBookKeys(action.state.selectedBookKeys),
        minPlaySize: coerceMinPlaySize(action.state.minPlaySize),
      };

    case "TOGGLE_BOOK": {
      const bookKey = normalizeWhaleWatchBookKey(action.bookKey);
      if (!bookKey) return state;
      const currentSelected = normalizeSelectedBookKeys(state.selectedBookKeys);
      const selectedBookKeys = currentSelected.includes(bookKey)
        ? currentSelected.filter((k) => k !== bookKey)
        : [...currentSelected, bookKey];
      return { ...state, selectedBookKeys };
    }

    case "CLEAR_BOOKS":
      return { ...state, selectedBookKeys: [] };

    case "SET_MARKET_TYPE":
      return { ...state, marketTypeFilter: action.value, selectedMarketNames: [] };

    case "TOGGLE_MARKET_NAME": {
      const { marketName } = action;
      const selectedMarketNames = state.selectedMarketNames.includes(marketName)
        ? state.selectedMarketNames.filter((m) => m !== marketName)
        : [...state.selectedMarketNames, marketName];
      return { ...state, selectedMarketNames };
    }

    case "CLEAR_MARKET_NAMES":
      return { ...state, selectedMarketNames: [] };

    default:
      return state;
  }
}


export type WhaleWatchPersistedFilters = { v: 1; state: WhaleWatchFilterState };

const VALID_MARKET_TYPES = new Set(["all", "game", "player"]);

export function coerceAmericanOddsBound(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isInteger(value)) return null;
  return Math.abs(value) >= 100 ? value : null;
}

export function parseAmericanOddsInput(text: string): number | null {
  return coerceAmericanOddsBound(Number(text.replace(/[+\s]/g, "")) || null);
}

export function formatAmericanOddsBound(value: number | null): string {
  if (value === null) return "";
  return value > 0 ? `+${value}` : String(value);
}

export function orderOddsBounds(minOdds: number | null, maxOdds: number | null): { minOdds: number | null; maxOdds: number | null } {
  return minOdds !== null && maxOdds !== null && minOdds > maxOdds
    ? { minOdds: maxOdds, maxOdds: minOdds }
    : { minOdds, maxOdds };
}

export function isWithinOddsBounds(odds: number, minOdds: number | null, maxOdds: number | null): boolean {
  if (minOdds !== null && odds < minOdds) return false;
  if (maxOdds !== null && odds > maxOdds) return false;
  return true;
}

function fromRawFilterFields(raw: Record<string, unknown>): WhaleWatchFilterState {
  const selectedBookKeys = Array.isArray(raw.selectedBookKeys) && raw.selectedBookKeys.every((k: unknown) => typeof k === "string")
    ? normalizeSelectedBookKeys(raw.selectedBookKeys.filter((k): k is string => typeof k === "string"))
    : FILTER_DEFAULTS.selectedBookKeys;
  const selectedMarketNames = Array.isArray(raw.selectedMarketNames) && raw.selectedMarketNames.every((m: unknown) => typeof m === "string")
    ? raw.selectedMarketNames.filter((m): m is string => typeof m === "string")
    : FILTER_DEFAULTS.selectedMarketNames;
  const marketTypeFilter = typeof raw.marketTypeFilter === "string" && VALID_MARKET_TYPES.has(raw.marketTypeFilter)
    ? raw.marketTypeFilter
    : FILTER_DEFAULTS.marketTypeFilter;
  const rawGameTime = raw.gameTime !== null && typeof raw.gameTime === "object" && !Array.isArray(raw.gameTime)
    ? raw.gameTime as Record<string, unknown>
    : null;
  const gameTime = rawGameTime
    ? { days: coerceGameDays(rawGameTime.days), within: coerceStartsWithin(rawGameTime.within), live: false }
    : FILTER_DEFAULTS.gameTime;

  return {
    searchQuery: typeof raw.searchQuery === "string" ? raw.searchQuery : FILTER_DEFAULTS.searchQuery,
    minEdge: typeof raw.minEdge === "number" && Number.isFinite(raw.minEdge) ? raw.minEdge : FILTER_DEFAULTS.minEdge,
    minWhaleVolume: typeof raw.minWhaleVolume === "number" && Number.isFinite(raw.minWhaleVolume) ? raw.minWhaleVolume : FILTER_DEFAULTS.minWhaleVolume,
    minPlaySize: coerceMinPlaySize(raw.minPlaySize),
    minOdds: coerceAmericanOddsBound(raw.minOdds),
    maxOdds: coerceAmericanOddsBound(raw.maxOdds),
    sortMode: raw.sortMode === "edge" || raw.sortMode === "whale" ? raw.sortMode : FILTER_DEFAULTS.sortMode,
    selectedBookKeys,
    sportFilter: typeof raw.sportFilter === "string" ? raw.sportFilter.toLowerCase() : FILTER_DEFAULTS.sportFilter,
    marketTypeFilter,
    selectedMarketNames,
    gameTime,
  };
}

export function coerceWhaleWatchFilterState(
  value: unknown
): WhaleWatchFilterState | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const obj = value as Record<string, unknown>;

  if (obj.v !== 1 || !obj.state || typeof obj.state !== "object" || Array.isArray(obj.state)) return null;

  return fromRawFilterFields(obj.state as Record<string, unknown>);
}

const WHALE_WATCH_VIEW_PARAM_KEYS = new Set([
  "q",
  "min_edge",
  "min_whale",
  "min_size",
  "min_odds",
  "max_odds",
  "sort",
  "books",
  "sport",
  "market_type",
  "markets",
  "game_date",
  "starts_within",
]);

function parseNumberParam(raw: string | null): number | null {
  if (raw == null || raw === "") return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
}

function parseCsvParam(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(",").map((part) => part.trim()).filter((part) => part.length > 0);
}

export function hasWhaleWatchViewQueryParams(search: string | URLSearchParams): boolean {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  for (const key of WHALE_WATCH_VIEW_PARAM_KEYS) {
    if (params.has(key)) return true;
  }
  return false;
}

export function hasWhaleWatchBooksQueryParam(search: string | URLSearchParams): boolean {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  return params.has("books");
}

export function sameSelectedBookKeys(a: readonly string[], b: readonly string[]): boolean {
  const left = normalizeSelectedBookKeys([...a]);
  const right = normalizeSelectedBookKeys([...b]);
  if (left.length !== right.length) return false;
  const rightSet = new Set(right);
  return left.every((book) => rightSet.has(book));
}

function parseBooksParam(raw: string | null): string[] {
  if (raw === null) return FILTER_DEFAULTS.selectedBookKeys;
  if (raw === "" || raw === "none") return [];
  return parseCsvParam(raw);
}

export function parseWhaleWatchViewSearch(
  search: string | URLSearchParams,
): WhaleWatchFilterState {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const minEdge = parseNumberParam(params.get("min_edge"));
  const minWhaleVolume = parseNumberParam(params.get("min_whale"));
  const minPlaySize = parseNumberParam(params.get("min_size"));
  const odds = orderOddsBounds(
    coerceAmericanOddsBound(parseNumberParam(params.get("min_odds"))),
    coerceAmericanOddsBound(parseNumberParam(params.get("max_odds"))),
  );

  return fromRawFilterFields({
    searchQuery: params.get("q") ?? FILTER_DEFAULTS.searchQuery,
    minEdge: minEdge ?? FILTER_DEFAULTS.minEdge,
    minWhaleVolume: minWhaleVolume ?? FILTER_DEFAULTS.minWhaleVolume,
    minPlaySize: minPlaySize ?? FILTER_DEFAULTS.minPlaySize,
    minOdds: odds.minOdds,
    maxOdds: odds.maxOdds,
    sortMode: params.get("sort"),
    selectedBookKeys: parseBooksParam(params.get("books")),
    sportFilter: params.get("sport") ?? FILTER_DEFAULTS.sportFilter,
    marketTypeFilter: params.get("market_type"),
    selectedMarketNames: parseCsvParam(params.get("markets")),
    gameTime: { days: params.getAll("game_date"), within: params.get("starts_within") },
  });
}

export function serializeWhaleWatchViewSearch(
  state: WhaleWatchFilterState,
  defaultBookKeys: readonly string[] = FILTER_DEFAULTS.selectedBookKeys,
): string {
  const params = new URLSearchParams();
  const query = state.searchQuery.trim();
  if (query) params.set("q", query);
  if (state.minEdge !== FILTER_DEFAULTS.minEdge) params.set("min_edge", String(state.minEdge));
  if (state.minWhaleVolume !== FILTER_DEFAULTS.minWhaleVolume) params.set("min_whale", String(state.minWhaleVolume));
  if (state.minPlaySize !== FILTER_DEFAULTS.minPlaySize) params.set("min_size", String(state.minPlaySize));
  if (state.minOdds !== null) params.set("min_odds", String(state.minOdds));
  if (state.maxOdds !== null) params.set("max_odds", String(state.maxOdds));
  if (state.sortMode !== FILTER_DEFAULTS.sortMode) params.set("sort", state.sortMode);
  const books = normalizeSelectedBookKeys(state.selectedBookKeys);
  if (!sameSelectedBookKeys(books, defaultBookKeys)) {
    params.set("books", books.length === 0 ? "none" : books.join(","));
  }
  if (state.sportFilter !== FILTER_DEFAULTS.sportFilter) params.set("sport", state.sportFilter);
  if (state.marketTypeFilter !== FILTER_DEFAULTS.marketTypeFilter) params.set("market_type", state.marketTypeFilter);
  if (state.selectedMarketNames.length > 0) params.set("markets", state.selectedMarketNames.join(","));
  for (const day of state.gameTime.days) params.append("game_date", day);
  if (state.gameTime.within > 0) params.set("starts_within", String(state.gameTime.within));

  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}


export function hasGameTimeFilter(time: GameTime, todayYmd: string = localTodayYmd()): boolean {
  return time.within > 0 || activeGameDays(time.days, todayYmd).length > 0;
}

export function countActiveFilters(f: WhaleWatchFilterState): number {
  let count = 0;
  if (f.selectedBookKeys.length > 0) count++;
  if (f.searchQuery.trim().length > 0) count++;
  if (f.minEdge !== FILTER_DEFAULTS.minEdge) count++;
  if (f.minWhaleVolume !== FILTER_DEFAULTS.minWhaleVolume) count++;
  if (f.minPlaySize !== FILTER_DEFAULTS.minPlaySize) count++;
  if (f.minOdds !== null || f.maxOdds !== null) count++;
  if (f.sortMode !== FILTER_DEFAULTS.sortMode) count++;
  if (f.sportFilter !== FILTER_DEFAULTS.sportFilter) count++;
  if (f.marketTypeFilter !== FILTER_DEFAULTS.marketTypeFilter) count++;
  if (f.selectedMarketNames.length > 0) count++;
  if (hasGameTimeFilter(f.gameTime)) count++;
  return count;
}

export function countExtraFilters(
  f: WhaleWatchFilterState,
  folded: { search: boolean; sport: boolean; date: boolean },
): number {
  let count = 0;
  if (folded.sport && f.sportFilter !== FILTER_DEFAULTS.sportFilter) count++;
  if (folded.date && hasGameTimeFilter(f.gameTime)) count++;
  if (f.minEdge !== FILTER_DEFAULTS.minEdge) count++;
  if (f.minWhaleVolume !== FILTER_DEFAULTS.minWhaleVolume) count++;
  if (f.minPlaySize !== FILTER_DEFAULTS.minPlaySize) count++;
  if (f.minOdds !== null || f.maxOdds !== null) count++;
  if (f.marketTypeFilter !== FILTER_DEFAULTS.marketTypeFilter) count++;
  if (f.selectedMarketNames.length > 0) count++;
  if (f.sortMode !== FILTER_DEFAULTS.sortMode) count++;
  if (folded.search && f.searchQuery.trim().length > 0) count++;
  return count;
}

export function hasResultSuppressingFilters(f: WhaleWatchFilterState): boolean {
  return (
    f.selectedBookKeys.length > 0 ||
    f.searchQuery.trim().length > 0 ||
    f.minEdge > FILTER_DEFAULTS.minEdge ||
    f.minWhaleVolume > FILTER_DEFAULTS.minWhaleVolume ||
    f.minPlaySize > FILTER_DEFAULTS.minPlaySize ||
    f.minOdds !== null ||
    f.maxOdds !== null ||
    f.sportFilter !== FILTER_DEFAULTS.sportFilter ||
    f.marketTypeFilter !== FILTER_DEFAULTS.marketTypeFilter ||
    f.selectedMarketNames.length > 0 ||
    hasGameTimeFilter(f.gameTime)
  );
}

export function canResetWhaleWatchEmptyState(
  f: WhaleWatchFilterState,
  heldRowCount: number,
): boolean {
  return hasResultSuppressingFilters(f) && heldRowCount > 0;
}

export function getNeighborId(ids: readonly string[], id: string): string | null {
  const index = ids.indexOf(id);
  if (index < 0) return null;
  return ids[index + 1] ?? ids[index - 1] ?? null;
}

export function resolveSelectedId(
  ids: readonly string[],
  selectedId: string | null,
): string | null {
  if (ids.length === 0) return selectedId;
  if (selectedId !== null && ids.includes(selectedId)) return selectedId;
  return ids[0];
}

const TRACKED_FILTER_NAMES: Partial<Record<keyof WhaleWatchFilterState, string>> = {
  searchQuery: "search",
  minEdge: "min_edge",
  minWhaleVolume: "min_whale",
  minPlaySize: "min_size",
  minOdds: "min_odds",
  maxOdds: "max_odds",
  selectedBookKeys: "sportsbook",
  sportFilter: "sport",
  marketTypeFilter: "market_type",
  selectedMarketNames: "markets",
  gameTime: "date",
};

export type WhaleWatchFilterSource = FilterChangeSource | "view";

export type WhaleWatchFilterSurface = "bar" | "sheet";

export function getWhaleWatchFilterSource(
  action: WhaleWatchFiltersAction,
  surface: WhaleWatchFilterSurface = "bar",
): WhaleWatchFilterSource {
  if (action.type === "HYDRATE") return "view";
  if (action.type === "RESET_ALL") return "clear_all";
  return surface;
}

function filterValue(state: WhaleWatchFilterState, key: keyof WhaleWatchFilterState): string {
  if (key === "gameTime") return gameTimeValue(state.gameTime);
  const value = state[key];
  return Array.isArray(value) ? value.join(",") : String(value ?? "");
}

export function getWhaleWatchFilterChanges(
  prev: WhaleWatchFilterState,
  next: WhaleWatchFilterState,
): Array<{ filter_name: string; filter_value: string }> {
  const changes: Array<{ filter_name: string; filter_value: string }> = [];
  for (const [key, filterName] of Object.entries(TRACKED_FILTER_NAMES) as Array<[keyof WhaleWatchFilterState, string]>) {
    const value = filterValue(next, key);
    const previous = filterValue(prev, key);
    if (value !== previous) changes.push({ filter_name: filterName, filter_value: value });
  }
  return changes;
}
